import { Box, Button, Card, Divider, Grid, Typography } from '@mui/material';
import { CheckRounded as CheckRoundedIcon } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import NumberFormat from 'react-number-format';
import { GenericProductDto, PackageV2Dto, ProductBillingTypeEnum, ProductV2Dto } from '@inosio/payments-sdk';
import { calculateCostFromTiers } from '../../../shared/util/calculators';

export interface PackageCardProps {
  PackageDto: PackageV2Dto;
  Products: ProductV2Dto[] | undefined;
  DoorCount: number;
  IsCustom: boolean; // bit flags that tells us if the package is custom
  OnButtonClick: () => void;
}

export default function PackageCard(props: PackageCardProps) {
  const calculatePackageCost = (): number => {
    let totalCost = 0;

    // Process each product in the package
    props.PackageDto.PackageMetadata?.IncludedProducts.forEach((product) => {
      product.Payload.forEach((subProd) => {
        if (subProd && subProd.ProductBillingType.includes(ProductBillingTypeEnum.FlatMonthlyFee)) {
          const productPayload = subProd as GenericProductDto; // flat monthly type products will always be of this type
          //if the pricing payload has this property
          if (!!productPayload.FlatFeeTiers?.length) {
            // Use the generalized tier calculation function
            const tierCost = calculateCostFromTiers(productPayload.FlatFeeTiers, props.DoorCount);
            totalCost += tierCost;
          }
        }
      });
    });

    // Apply package-level discounts
    if (props.PackageDto.PackageMetadata?.Discounts?.length) {
      props.PackageDto.PackageMetadata.Discounts.forEach((discount) => {
        const discountTiers = discount.Tiers;
        const discountAmount = calculateCostFromTiers(discountTiers, props.DoorCount);
        totalCost -= discountAmount;
      });
    }

    const costInMajorUnits = totalCost / 100;

    return costInMajorUnits;
  };

  return (
    <Card
      sx={{
        boxShadow: '0px 0px 40px rgba(0, 0, 0, 0.1)',
        padding: 2,
        borderRadius: '15px',
        height: '800px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      {/* Top Section */}
      <Typography sx={{ fontFamily: 'Work Sans !important', fontSize: 'large', textAlign: 'center' }}>{props.PackageDto?.PackageLabel}</Typography>
      {!props.IsCustom ? (
        props.PackageDto?.PackageMetadata?.TileCategories?.map((prod, i) => {
          return (
            <Box sx={{ mt: 2 }} key={i}>
              <Typography sx={{ fontFamily: 'Work Sans !important', mb: 1 }}>{prod.Title}</Typography>
              {prod.Features.map((feat, j) => {
                return (
                  <Grid container columnSpacing={2} key={j}>
                    <Grid item>{feat.Included ? <CheckRoundedIcon /> : <CloseIcon />}</Grid>
                    <Grid item>
                      <Typography sx={{ fontFamily: 'Work Sans !important' }}>{feat.Name}</Typography>
                    </Grid>
                  </Grid>
                );
              })}
            </Box>
          );
        })
      ) : (
        <Grid container sx={{ mt: 2, p: 2 }} rowSpacing={12}>
          <Grid item xs={12}>
            <Typography sx={{ mb: 2, fontFamily: 'Work Sans !important', fontSize: 'large', textAlign: 'center' }}>{props.PackageDto?.PackageMetadata?.CustomTileTitle}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography sx={{ fontFamily: 'Work Sans !important', fontSize: 'medium', textAlign: 'left' }}>{props.PackageDto?.PackageMetadata?.CustomTileBody}</Typography>
          </Grid>
        </Grid>
      )}

      {/* Bottom Section */}
      <Box sx={{ mt: 'auto' }}>
        {!props.IsCustom && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography
              sx={{
                fontFamily: 'Work Sans !important',
                fontSize: '35px',
                fontWeight: 'bold',
                textAlign: 'center',
              }}
            >
              <NumberFormat
                thousandsGroupStyle="thousand"
                value={calculatePackageCost()}
                prefix="$"
                decimalSeparator="."
                displayType="text"
                suffix="/month"
                type="text"
                thousandSeparator={true}
                allowNegative={true}
                decimalScale={0}
                fixedDecimalScale={true}
              />
            </Typography>
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: props.IsCustom ? 0 : 2 }}>
          <Button
            variant="outlined"
            sx={{
              color: '#39A2DB',
              border: '2px solid #39A2DB',
              boxShadow: 'none',
              borderRadius: '24px',
              fontFamily: "'Open Sans', sans-serif",
              fontSize: '14px',
              fontWeight: 700,
              textTransform: 'capitalize',
              '&:hover': {
                backgroundColor: '#39A2DB',
                color: 'white',
                border: '2px solid #39A2DB',
              },
              width: '80%',
              height: '50px',
            }}
            onClick={props.OnButtonClick}
          >
            {props.IsCustom ? 'Start Quote' : 'Get Started'}
          </Button>
        </Box>
      </Box>
    </Card>
  );
}
